import React, { useState } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { Droppable } from '@hello-pangea/dnd';
import { doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import WorkspaceHeader from './workspace/WorkspaceHeader';
import DeleteWorkspaceModal from './workspace/DeleteWorkspaceModal';
import ManageWorkspaceMembersModal from './ManageWorkspaceMembersModal';
import EditWorkspaceModal from './workspace/EditWorkspaceModal';
import { SectionContainer, ProjectsGrid } from '../styles/WorkspaceSection.styles';

function WorkspaceSection({
    workspace,
    projects,
    onMoveProject,
    onToggleStar,
    starredProjectIds,
    onArchive,
    renderProjectCard
}) {
    const [showSettings, setShowSettings] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [isManagingMembers, setIsManagingMembers] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [isCollapsed, setIsCollapsed] = useLocalStorage(`workspace-collapsed-${workspace.id}`, false);
    const [isSingleRow, setIsSingleRow] = useLocalStorage(`workspace-single-row-${workspace.id}`, false);

    const handleDeleteWorkspace = async () => {
        try {
            const updatePromises = projects.map(project =>
                updateDoc(doc(db, 'projects', project.id), {
                    workspaceId: null,
                    updatedAt: new Date().toISOString()
                })
            );

            await Promise.all(updatePromises);
            await deleteDoc(doc(db, 'workspaces', workspace.id));
            setShowDeleteConfirm(false);
            setShowSettings(false);
        } catch (error) {
            console.error('Error deleting workspace:', error);
        }
    };

    const handleMoveAllProjects = async () => {
        try {
            const updatePromises = projects.map(project =>
                updateDoc(doc(db, 'projects', project.id), {
                    workspaceId: null,
                    updatedAt: new Date().toISOString()
                })
            );

            await Promise.all(updatePromises);
            await updateDoc(doc(db, 'workspaces', workspace.id), {
                projects: [],
                updatedAt: new Date().toISOString()
            });

            setShowSettings(false);
        } catch (error) {
            console.error('Error moving projects:', error);
        }
    };

    const handleEditWorkspace = () => {
        setShowEditModal(true);
        setShowSettings(false);
    };

    const handleUpdateWorkspace = async (formData) => {
        try {
            await updateDoc(doc(db, 'workspaces', workspace.id), {
                name: formData.name,
                description: formData.description,
                updatedAt: new Date().toISOString()
            });
            setShowEditModal(false);
        } catch (error) {
            console.error('Error updating workspace:', error);
        }
    };

    return (
        <SectionContainer>
            <WorkspaceHeader
                workspace={workspace}
                projectCount={projects.length}
                showSettings={showSettings}
                setShowSettings={setShowSettings}
                onSettingsClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowSettings(!showSettings);
                }}
                onManageMembers={() => {
                    setIsManagingMembers(true);
                    setShowSettings(false);
                }}
                onMoveProjects={handleMoveAllProjects}
                onDelete={() => setShowDeleteConfirm(true)}
                onEditWorkspace={handleEditWorkspace}
                isCollapsed={isCollapsed}
                setIsCollapsed={setIsCollapsed}
                isSingleRow={isSingleRow}
                setIsSingleRow={setIsSingleRow}
            />
            {showEditModal && (
                <EditWorkspaceModal
                    workspace={workspace}
                    onClose={() => setShowEditModal(false)}
                    onUpdate={handleUpdateWorkspace}
                />
            )}

            {showDeleteConfirm && (
                <DeleteWorkspaceModal
                    onClose={() => setShowDeleteConfirm(false)}
                    onConfirm={handleDeleteWorkspace}
                />
            )}
            {isManagingMembers && (
                <ManageWorkspaceMembersModal
                    workspace={workspace}
                    onClose={() => {
                        setIsManagingMembers(false);
                        setShowSettings(false);
                    }}
                />
            )}
            {!isCollapsed && (
                <Droppable droppableId={`workspace-${workspace.id}`} direction="horizontal" type="PROJECT">
                    {(provided) => (
                        <ProjectsGrid
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={isSingleRow ? 'single-row' : ''}
                        >
                            {projects.map((project, index) =>
                                renderProjectCard(project, index, `workspace-${workspace.id}`)
                            )}
                            {provided.placeholder}
                        </ProjectsGrid>
                    )}
                </Droppable>
            )}
        </SectionContainer>
    );
}

export default WorkspaceSection;
